import React, { useState, useMemo } from 'react';
import { 
  Receipt, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit,
  Download,
  Send,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  User,
  Building,
  CreditCard,
  Activity,
  ArrowLeft,
  BarChart3,
  TrendingUp,
  Mail,
  Phone
} from 'lucide-react';

// Mock invoice data
const invoices = [
  {
    id: 'invoice-001',
    invoice_number: 'INV-2025-001',
    sales_order_id: 'SO-2025-001',
    customer_id: 'cust-001',
    customer_name: '台灣電子科技股份有限公司',
    customer_code: 'TETC-001',
    invoice_date: '2025-01-21',
    due_date: '2025-02-20',
    payment_terms: '30天',
    status: 'sent',
    total_amount: 3850000,
    tax_amount: 385000,
    net_amount: 3465000,
    currency: 'TWD',
    payment_status: 'pending',
    created_by: '財務部_王小姐',
    created_date: '2025-01-21',
    sent_date: '2025-01-21',
    billing_address: {
      street: '台北市信義區信義路五段7號',
      city: '台北市',
      postal_code: '110',
      country: '台灣'
    },
    contact_person: {
      name: '李經理',
      phone: '02-2345-6789',
      email: 'manager.li@tetc.com.tw'
    },
    items: [
      {
        item_id: 'item-001',
        product_name: '鋁合金材料 A-grade',
        quantity: 500,
        unit: '公斤',
        unit_price: 50,
        total_price: 25000,
        tax_rate: 0.1
      },
      {
        item_id: 'item-002',
        product_name: '鋁合金材料 B-grade',
        quantity: 300,
        unit: '公斤',
        unit_price: 45,
        total_price: 13500,
        tax_rate: 0.1
      }
    ],
    notes: '感謝您的訂購，請於到期日前完成付款',
    delivery_reference: 'DL-2025-001',
    payment_method: 'bank_transfer',
    bank_details: {
      bank_name: '台灣銀行',
      account_number: '123-456-789',
      account_name: '我們公司股份有限公司'
    }
  },
  {
    id: 'invoice-002',
    invoice_number: 'INV-2025-002',
    sales_order_id: 'SO-2025-002',
    customer_id: 'cust-002',
    customer_name: '創新科技股份有限公司',
    customer_code: 'INNOV-002',
    invoice_date: '2025-01-22',
    due_date: '2025-03-08',
    payment_terms: '45天',
    status: 'draft',
    total_amount: 935000,
    tax_amount: 85000,
    net_amount: 850000,
    currency: 'TWD',
    payment_status: 'pending',
    created_by: '財務部_張先生',
    created_date: '2025-01-22',
    sent_date: null,
    billing_address: {
      street: '新竹市東區光復路二段101號',
      city: '新竹市',
      postal_code: '300',
      country: '台灣'
    },
    contact_person: {
      name: '張主任',
      phone: '03-5678-901',
      email: 'director.zhang@innov.com.tw'
    },
    items: [
      {
        item_id: 'item-003',
        product_name: '精密零件 Type-A',
        quantity: 100,
        unit: '件',
        unit_price: 850,
        total_price: 85000,
        tax_rate: 0.1
      }
    ],
    notes: '精密零件訂購，請確認規格無誤',
    delivery_reference: 'DL-2025-002',
    payment_method: 'bank_transfer',
    bank_details: {
      bank_name: '台灣銀行',
      account_number: '123-456-789',
      account_name: '我們公司股份有限公司'
    }
  }
];

const InvoiceManagement = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  const formatCurrency = (amount, currency = 'TWD') => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-gray-100 text-gray-800',
      'sent': 'bg-blue-100 text-blue-800',
      'paid': 'bg-green-100 text-green-800',
      'overdue': 'bg-red-100 text-red-800',
      'cancelled': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'draft': '草稿',
      'sent': '已發送',
      'paid': '已付款',
      'overdue': '逾期',
      'cancelled': '已取消'
    };
    return labels[status] || status;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'partial': 'bg-orange-100 text-orange-800',
      'paid': 'bg-green-100 text-green-800',
      'overdue': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      'pending': '待付款',
      'partial': '部分付款',
      'paid': '已付款',
      'overdue': '逾期未付'
    };
    return labels[status] || status;
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      if (statusFilter !== 'all' && invoice.status !== statusFilter) {
        return false;
      }
      if (paymentStatusFilter !== 'all' && invoice.payment_status !== paymentStatusFilter) {
        return false;
      }
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          invoice.invoice_number.toLowerCase().includes(searchLower) ||
          invoice.customer_name.toLowerCase().includes(searchLower) ||
          invoice.customer_code.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [statusFilter, paymentStatusFilter, searchTerm]);

  const renderWorkflowBanner = () => {
    if (navigationSource === 'sales-workflow') {
      return (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-800">來自業務流程: 銷售週期管理</h3>
                <p className="text-sm text-purple-600">階段E: 發票開立與款項收取 - 發票管理</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回銷售週期</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderDashboard = () => {
    const totalInvoices = invoices.length;
    const sentInvoices = invoices.filter(i => i.status === 'sent').length;
    const paidInvoices = invoices.filter(i => i.payment_status === 'paid').length;
    const pendingInvoices = invoices.filter(i => i.payment_status === 'pending').length;
    const totalAmount = invoices.reduce((sum, i) => sum + i.total_amount, 0);
    const pendingAmount = invoices.filter(i => i.payment_status === 'pending').reduce((sum, i) => sum + i.total_amount, 0);

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">發票管理儀表板</h2>
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增發票</span>
          </button>
        </div>

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">發票總數</p>
                <p className="text-2xl font-bold text-gray-800">{totalInvoices}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Receipt className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">已發送</p>
                <p className="text-2xl font-bold text-gray-800">{sentInvoices}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Send className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">發票總額</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalAmount)}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">待收款項</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(pendingAmount)}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* 發票狀態分佈 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">發票狀態分佈</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">草稿</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-gray-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">1</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">已發送</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">1</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">已付款</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <span className="text-sm font-medium">0</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">付款狀態分佈</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">待付款</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                  <span className="text-sm font-medium">2</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">部分付款</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-orange-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <span className="text-sm font-medium">0</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">已付款</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <span className="text-sm font-medium">0</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 最新發票 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">最新發票</h3>
              <button
                onClick={() => setActiveView('list')}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                查看全部
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedInvoice(invoice);
                    setActiveView('detail');
                  }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <Receipt className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{invoice.customer_name}</h4>
                      <p className="text-sm text-gray-600">{invoice.invoice_number} - {invoice.invoice_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-semibold text-gray-800">{formatCurrency(invoice.total_amount)}</p>
                      <p className="text-sm text-gray-600">到期日: {invoice.due_date}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                      {getStatusLabel(invoice.status)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(invoice.payment_status)}`}>
                      {getPaymentStatusLabel(invoice.payment_status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderList = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">發票列表</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增發票</span>
          </button>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回儀表板</span>
          </button>
        </div>
      </div>

      {/* 搜尋和篩選 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋發票編號或客戶..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有狀態</option>
            <option value="draft">草稿</option>
            <option value="sent">已發送</option>
            <option value="paid">已付款</option>
            <option value="overdue">逾期</option>
            <option value="cancelled">已取消</option>
          </select>
          
          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有付款狀態</option>
            <option value="pending">待付款</option>
            <option value="partial">部分付款</option>
            <option value="paid">已付款</option>
            <option value="overdue">逾期未付</option>
          </select>
        </div>
      </div>

      {/* 發票表格 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                發票資訊
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                客戶
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                發票日期
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                金額
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                狀態
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                付款狀態
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredInvoices.map((invoice) => (
              <tr key={invoice.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-2 rounded-full mr-3">
                      <Receipt className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{invoice.invoice_number}</div>
                      <div className="text-sm text-gray-500">{invoice.sales_order_id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{invoice.customer_name}</div>
                  <div className="text-sm text-gray-500">{invoice.customer_code}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{invoice.invoice_date}</div>
                  <div className="text-sm text-gray-500">到期: {invoice.due_date}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(invoice.total_amount)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                    {getStatusLabel(invoice.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(invoice.payment_status)}`}>
                    {getPaymentStatusLabel(invoice.payment_status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedInvoice(invoice);
                      setActiveView('detail');
                    }}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="text-green-600 hover:text-green-900 mr-3">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button className="text-purple-600 hover:text-purple-900">
                    <Download className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDetail = () => {
    if (!selectedInvoice) return null;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">發票詳細資訊</h2>
          <div className="flex items-center space-x-4">
            <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              <Download className="w-4 h-4" />
              <span>下載PDF</span>
            </button>
            <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              <Send className="w-4 h-4" />
              <span>發送發票</span>
            </button>
            <button
              onClick={() => setActiveView('list')}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回列表</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 基本資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">基本資訊</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">發票編號:</span>
                <span className="font-medium">{selectedInvoice.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">銷售訂單:</span>
                <span className="font-medium">{selectedInvoice.sales_order_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">發票日期:</span>
                <span className="font-medium">{selectedInvoice.invoice_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">到期日期:</span>
                <span className="font-medium">{selectedInvoice.due_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">付款條件:</span>
                <span className="font-medium">{selectedInvoice.payment_terms}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">發票狀態:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedInvoice.status)}`}>
                  {getStatusLabel(selectedInvoice.status)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">付款狀態:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(selectedInvoice.payment_status)}`}>
                  {getPaymentStatusLabel(selectedInvoice.payment_status)}
                </span>
              </div>
            </div>
          </div>

          {/* 客戶資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">客戶資訊</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">客戶名稱:</span>
                <span className="font-medium">{selectedInvoice.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">客戶代碼:</span>
                <span className="font-medium">{selectedInvoice.customer_code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">聯絡人:</span>
                <span className="font-medium">{selectedInvoice.contact_person.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">聯絡電話:</span>
                <span className="font-medium">{selectedInvoice.contact_person.phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">電子郵件:</span>
                <span className="font-medium">{selectedInvoice.contact_person.email}</span>
              </div>
              <div className="flex flex-col space-y-1">
                <span className="text-gray-600">帳單地址:</span>
                <span className="font-medium">
                  {selectedInvoice.billing_address.street}<br />
                  {selectedInvoice.billing_address.city} {selectedInvoice.billing_address.postal_code}<br />
                  {selectedInvoice.billing_address.country}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 發票項目 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">發票項目</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    產品名稱
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    數量
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    單價
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    稅率
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    金額
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedInvoice.items.map((item) => (
                  <tr key={item.item_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.product_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.quantity.toLocaleString()} {item.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(item.unit_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(item.tax_rate * 100).toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(item.total_price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 金額明細 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">金額明細</h3>
          <div className="space-y-4">
            <div className="flex justify-between text-lg">
              <span className="text-gray-600">小計:</span>
              <span className="font-medium">{formatCurrency(selectedInvoice.net_amount)}</span>
            </div>
            <div className="flex justify-between text-lg">
              <span className="text-gray-600">稅額:</span>
              <span className="font-medium">{formatCurrency(selectedInvoice.tax_amount)}</span>
            </div>
            <div className="border-t pt-4">
              <div className="flex justify-between text-xl font-bold">
                <span className="text-gray-800">總計:</span>
                <span className="text-gray-800">{formatCurrency(selectedInvoice.total_amount)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 付款資訊 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">付款資訊</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">付款方式</h4>
              <p className="text-gray-600">銀行轉帳</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 mb-2">銀行資訊</h4>
              <div className="text-gray-600">
                <p>銀行: {selectedInvoice.bank_details.bank_name}</p>
                <p>帳號: {selectedInvoice.bank_details.account_number}</p>
                <p>戶名: {selectedInvoice.bank_details.account_name}</p>
              </div>
            </div>
          </div>
        </div>

        {/* 備註 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">備註</h3>
          <p className="text-gray-600 bg-gray-50 p-3 rounded">{selectedInvoice.notes}</p>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderList();
      case 'detail':
        return renderDetail();
      case 'create':
        return (
          <div className="space-y-6">
            {renderWorkflowBanner()}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">新增發票</h2>
              <p className="text-gray-600">發票新增功能開發中...</p>
              <button
                onClick={() => setActiveView('dashboard')}
                className="mt-4 flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回儀表板</span>
              </button>
            </div>
          </div>
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default InvoiceManagement; 